import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setVehicleDetails } from 'store/reducers/vehicle.reducer';
import { AppDispatch } from 'store/store';
import { IRACVehicle, IUser, IVehiclePayload } from 'modules/core/types';
import { EMPTY_STRING, MODAL_DIALOG_ID, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { useModalState } from 'modules/core/hooks';
import { getDefaultVehiclePhoto } from 'store/selectors/vehicle.selector';
import { getUser } from 'store/selectors/auth.selector';
import { useDealSourceData } from 'hooks/useDealSourceData';
import { TableName } from 'modules/core/enums';

import { Container, ImageUI } from './styles';

interface IVehicleDetailDialogTriggerImage {
  data: IRACVehicle | IVehiclePayload;
  noColoring?: boolean;
  isLender?: boolean;
  isMakeItWork?: boolean;
}

export const ImageCell: FC<IVehicleDetailDialogTriggerImage> = ({
  data,
  noColoring,
  isLender = false,
  isMakeItWork = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const defaultVehiclePhoto = useSelector(getDefaultVehiclePhoto);
  const { dealSourceData } = useDealSourceData(isLender ? TableName.LENDER : TableName.RAC);
  const { handleOpenDialog } = useModalState([MODAL_DIALOG_ID.VEHICLE_DETAILS]);
  const user = useSelector(getUser) as IUser;
  const { blackCar, blackCarManuallyCreated, whiteCar, noPhoto } = STATIC_IMAGE_URLS.icons;

  const isImageExist = !!data.thumbnails?.length;

  const openVehicleDetailsDialog = useCallback(() => {
    dispatch(setVehicleDetails({ id: data.id, title: data.description }));
    handleOpenDialog();
  }, [handleOpenDialog, dispatch, data]);

  const getVehiclePhotoSrc = useCallback(() => {
    if (!user?.isWithInventory || data?.manually_created) {
      if (isMakeItWork && dealSourceData?.vehicle_id === data.id) {
        return whiteCar;
      }

      if (!!data.isStubbed && !!data.photo) {
        return data.photo as string;
      }

      if (data.manually_created) {
        return blackCarManuallyCreated;
      }

      return blackCar;
    }

    if (isImageExist) {
      return data.thumbnails[0];
    }

    return noPhoto;
  }, [
    blackCar,
    blackCarManuallyCreated,
    data?.manually_created,
    data.thumbnails,
    data.id,
    data.isStubbed,
    data.photo,
    isImageExist,
    noPhoto,
    dealSourceData,
    user?.isWithInventory,
    whiteCar,
    isMakeItWork,
  ]);

  const src = useMemo(() => getVehiclePhotoSrc(), [getVehiclePhotoSrc]);

  const [imageSrc, setImageSrc] = useState<string>(EMPTY_STRING);

  const handleError = useCallback(() => {
    if (defaultVehiclePhoto) setImageSrc(defaultVehiclePhoto);
    else setImageSrc(STATIC_IMAGE_URLS.icons.noPhoto);
  }, [defaultVehiclePhoto]);

  useEffect(() => {
    setImageSrc(EMPTY_STRING);
    const img = new Image();

    img.src = src;
    img.onload = () => {
      setImageSrc(src);
    };

    img.onerror = handleError;
  }, [handleError, src]);

  const dialogTriggerProps = useMemo(
    () =>
      [noPhoto, blackCar, blackCarManuallyCreated, whiteCar].includes(imageSrc) || !!data.isStubbed
        ? {}
        : { onClick: openVehicleDetailsDialog },
    [blackCar, blackCarManuallyCreated, imageSrc, noPhoto, openVehicleDetailsDialog, whiteCar, data.isStubbed],
  );

  return (
    <Container>
      {imageSrc.length ? (
        <ImageUI
          src={imageSrc}
          {...dialogTriggerProps}
          onError={handleError}
          isImage={![noPhoto, blackCar, blackCarManuallyCreated, whiteCar].includes(imageSrc) && !data.isStubbed}
        />
      ) : (
        <></>
      )}
    </Container>
  );
};
